/**
 * SAM Chat Helper Functions
 *
 * Utilities for parsing and validating SAM chat API responses
 */

interface SuggestedEstimate {
  company_count: number;
  reasoning: string;
  confidence: "low" | "medium" | "high";
}

interface SAMChatResponse {
  success: boolean;
  response: string;
  conversation_complete: boolean;
  suggested_estimate?: SuggestedEstimate | null;
  final_estimate?: SuggestedEstimate | null;
  follow_up_questions?: string[];
}

/**
 * Validates and extracts the message from a SAM chat response
 * Handles cases where the response might be JSON or malformed
 */
export function extractSAMChatMessage(data: any): string {
  // Check if we have a valid response structure
  if (!data || typeof data !== "object") {
    // Invalid SAM chat response structure
    return "I apologize, but I received an invalid response. Please try again.";
  }

  // Extract the message, handling various possible formats
  if (typeof data.response === "string" && data.response.trim()) {
    // Check if the response contains markdown JSON code block
    const jsonBlockMatch = data.response.match(/```json\s*([\s\S]*?)\s*```/);
    if (jsonBlockMatch) {
      try {
        const parsed = JSON.parse(jsonBlockMatch[1]);
        if (parsed.message) {
          // SAM response contained JSON in markdown code block, extracting message
          // Store the parsed data for later extraction
          data._parsedFromMarkdown = parsed;
          return parsed.message;
        }
      } catch (e) {
        // Failed to parse JSON from markdown block
      }
    }

    // Check if the response is accidentally JSON stringified
    if (data.response.startsWith("{") && data.response.endsWith("}")) {
      try {
        const parsed = JSON.parse(data.response);
        if (parsed.message) {
          // SAM response was JSON within response field, extracting message
          return parsed.message;
        }
      } catch (e) {
        // Not valid JSON, treat as regular string
      }
    }
    return data.response;
  }

  // Fallback: check if there's a message field directly
  if (typeof data.message === "string" && data.message.trim()) {
    return data.message;
  }

  // No valid message found in SAM chat response
  return "I apologize, but I couldn't process the response properly. Please try again.";
}

/**
 * Validates a SAM chat response structure
 */
export function validateSAMChatResponse(data: any): SAMChatResponse {
  if (!data || typeof data !== "object") {
    throw new Error("Invalid response structure");
  }

  // Ensure required fields exist
  const response: SAMChatResponse = {
    success: data.success === true,
    response: extractSAMChatMessage(data),
    conversation_complete: data.conversation_complete === true,
    suggested_estimate: null,
    final_estimate: null,
    follow_up_questions: [],
  };

  // Check if we extracted data from markdown block
  if (data._parsedFromMarkdown) {
    const parsed = data._parsedFromMarkdown;
    if (parsed.suggested_estimate) {
      data.suggested_estimate = parsed.suggested_estimate;
    }
    if (parsed.final_estimate) {
      data.final_estimate = parsed.final_estimate;
    }
    if (parsed.conversation_complete !== undefined) {
      response.conversation_complete = parsed.conversation_complete;
    }
    if (parsed.follow_up_questions) {
      response.follow_up_questions = parsed.follow_up_questions;
    }
  }

  // Validate suggested estimate if present
  if (data.suggested_estimate && typeof data.suggested_estimate === "object") {
    const estimate = data.suggested_estimate;
    if (
      typeof estimate.company_count === "number" &&
      typeof estimate.reasoning === "string" &&
      ["low", "medium", "high"].includes(estimate.confidence)
    ) {
      response.suggested_estimate = estimate;
    } else {
      // Invalid suggested_estimate structure
    }
  }

  // Validate final estimate if present
  if (data.final_estimate && typeof data.final_estimate === "object") {
    const estimate = data.final_estimate;
    if (
      typeof estimate.company_count === "number" &&
      typeof estimate.reasoning === "string" &&
      ["low", "medium", "high"].includes(estimate.confidence)
    ) {
      response.final_estimate = estimate;
    } else {
      // Invalid final_estimate structure
    }
  }

  // Validate follow-up questions
  if (Array.isArray(data.follow_up_questions)) {
    response.follow_up_questions = data.follow_up_questions.filter(
      (q: any) => typeof q === "string",
    );
  }

  return response;
}

/**
 * Logs SAM chat response details for debugging
 */
export function logSAMChatResponse(_action: string, _data: any): void {
  // SAM Chat logging removed - console statements cleaned up during quality improvement
}
